/**
 * Parts that render in or beneath the listbox, the popup list that the input
 * of a `Combobox` or an `Autocomplete` controls (`role="listbox"`). They read
 * only our own context (see `listboxContext.ts`), never Base UI's, so either
 * root can render them. Parts that wrap a Base UI component stay in each root,
 * styled from `listbox.module.css` so the two popups look alike.
 *
 * Internal to the foundation: consumers reach these through `Combobox.*` /
 * `Autocomplete.*`.
 */
import classNames from "classnames";
import { Plus as PlusIcon } from "lucide-react";
import * as React from "react";

import { SubstringHighlight } from "@/shared/text/SubstringHighlight";

import styles from "./listbox.module.css";
import { useListboxContext } from "./listboxContext";

export interface ListboxMatchTextProps {
  text: string;
}

/**
 * Text with the current query marked, as one `<span>`. Reads the query from
 * the root rather than taking it as a prop, so a custom row can't mark against
 * a query the list was never filtered by.
 */
export function MatchText({ text }: ListboxMatchTextProps) {
  const { highlightQuery } = useListboxContext("MatchText");

  // One element rather than the bare text-and-mark fragment, so a flex row
  // treats the text as one item instead of splitting the marked run from the
  // text around it.
  return (
    <span>
      <SubstringHighlight
        text={text}
        query={highlightQuery}
        markClassName={styles.mark}
      />
    </span>
  );
}

export interface ListboxFooterProps {
  /**
   * Nothing renders, rule included, while this is empty: `null`, `undefined`,
   * a Boolean, or `""`.
   */
  children?: React.ReactNode;
  className?: string;
}

/**
 * A ruled-off line beneath the list for whatever the caller wants to say about
 * it: a total, a hint.
 */
export function Footer({ children, className }: ListboxFooterProps) {
  if (
    children === null ||
    children === undefined ||
    typeof children === "boolean" ||
    children === ""
  ) {
    return null;
  }

  return <div className={classNames(styles.footer, className)}>{children}</div>;
}

export interface ListboxUseTypedProps {
  /** The text the row would use, shown quoted. */
  text: string;
}

/**
 * The content of a row that offers the typed text as it is, for a field whose
 * suggestions are hints rather than a closed set: a plus marker, then
 * Use “<text>”. Place it inside an `Item`; the caller decides whether and where
 * to offer the row.
 */
export function UseTyped({ text }: ListboxUseTypedProps) {
  return (
    <span className={styles.useTyped}>
      <PlusIcon className={styles.useTypedIcon} aria-hidden />
      <span>
        Use <b>“{text}”</b>
      </span>
    </span>
  );
}
